/**
 * FilterBar — estructura uniforme de filtros para todos los módulos.
 *
 *   <FilterBar left={<filtros de tipo>} right={<filtros de tiempo>} />
 *
 * - `left`  (filtros de TIPO) van a la izquierda dentro de un carril con
 *   scroll horizontal: si no caben, se hace scroll y aparecen flechas que
 *   avisan que es desplazable (ahorra espacio, una sola fila).
 * - `right` (filtros de TIEMPO) van a la derecha, fijos. Usa <DateMenu> para
 *   colapsar los date pickers a un icono de calendario.
 */
import { useEffect, useRef, useState } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'

/** Carril horizontal scrolleable con indicadores (flechas/degradado). */
export function ScrollRow({ children, gap = 12 }) {
  const ref = useRef(null)
  const [ov, setOv] = useState({ left: false, right: false })

  const update = () => {
    const el = ref.current
    if (!el) return
    setOv({
      left: el.scrollLeft > 2,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    })
  }

  useEffect(() => {
    update()
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(update)
    ro.observe(el)
    el.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      el.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [children])

  const nudge = (dir) => ref.current?.scrollBy({ left: dir * 200, behavior: 'smooth' })

  const arrow = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: 30,
    height: 30,
    borderRadius: 999,
    border: '1px solid #E6EFE3',
    background: '#fff',
    color: '#1E6E3A',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(20,80,42,0.12)',
  }
  const fade = (side) => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    [side]: 0,
    width: 44,
    pointerEvents: 'none',
    zIndex: 1,
    background: `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, #fff 30%, rgba(255,255,255,0))`,
  })

  return (
    <div className="relative min-w-0">
      {ov.left && <div style={fade('left')} />}
      {ov.left && (
        <button type="button" aria-label="Desplazar" onClick={() => nudge(-1)} style={{ ...arrow, left: 0 }}>
          <ChevronLeft size={16} />
        </button>
      )}
      <div
        ref={ref}
        className="overflow-x-auto scrollbar-thin"
        style={{ display: 'flex', alignItems: 'center', gap, scrollbarWidth: 'none' }}
      >
        {children}
      </div>
      {ov.right && <div style={fade('right')} />}
      {ov.right && (
        <button type="button" aria-label="Desplazar" onClick={() => nudge(1)} style={{ ...arrow, right: 0 }}>
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  )
}

/** Menú colapsado tras un icono de calendario, para filtros de tiempo. */
export function DateMenu({ children, label }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div className="relative flex-none" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Filtrar por fecha"
        className="inline-flex items-center gap-2"
        style={{
          height: 44,
          padding: label ? '0 16px' : '0 12px',
          borderRadius: 999,
          border: open ? '1.5px solid #1E6E3A' : '1.5px solid #E6EFE3',
          background: '#fff',
          color: '#1E6E3A',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        <Calendar size={18} />
        {label ? <span>{label}</span> : null}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            zIndex: 50,
            background: '#fff',
            border: '1px solid #E6EFE3',
            borderRadius: 16,
            boxShadow: '0 16px 40px rgba(20,80,42,0.16)',
            padding: 16,
            minWidth: 240,
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * FilterBar — los filtros con MÁS opciones van a la izquierda (`left`) y los
 * más cortos a la derecha (`right`). Cada lado scrollea de forma independiente
 * dentro de su propio carril (con flechas si no cabe). El derecho queda pegado
 * al borde derecho.
 */
export function FilterBar({ left, right }) {
  return (
    // En móvil se apila en 2 filas (un filtro por fila) para que ninguno quede
    // cortado; desde `sm` van lado a lado (tipo a la izquierda, tiempo a la derecha).
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full">
      <div className="w-full sm:flex-1 min-w-0">
        <ScrollRow>{left}</ScrollRow>
      </div>
      {/* El lado derecho (tiempo) NO va en ScrollRow: su overflow recortaría
          el popover del calendario (DateMenu). En móvil ocupa su propia fila. */}
      {right ? (
        <div className="w-full sm:w-auto sm:flex-none flex items-center gap-3 flex-wrap">
          {right}
        </div>
      ) : null}
    </div>
  )
}
